"use client";

import React, { useState } from "react";
import Image from "next/image";
import { getImageUrl } from "../../lib/util";

// Helper — Render structured text (bold, link, etc.)
function renderTextFromRichContent(content) {
  if (!content || !Array.isArray(content)) return null;

  // Recursive helper to handle nested children (for links, formatting, etc.)
  const renderChildren = (children) =>
    children?.map((child, i) => {
      if (child.type === "text") {
        let textElement = child.text;

        if (child.bold)
          textElement = (
            <strong key={i} className="font-semibold">
              {textElement}
            </strong>
          );
        if (child.italic)
          textElement = (
            <em key={i} className="italic">
              {textElement}
            </em>
          );
        if (child.underline)
          textElement = (
            <u key={i} className="underline underline-offset-2">
              {textElement}
            </u>
          );

        return <React.Fragment key={i}>{textElement}</React.Fragment>;
      }

      // Handle links like:
      // { "type": "link", "url": "...", "children": [{ "text": "Click here" }] }
      if (child.type === "link" && child.url) {
        return (
          <a
            key={i}
            href={child.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0096AC] underline hover:text-[#007d8f]"
          >
            {renderChildren(child.children)}
          </a>
        );
      }

      // If there are nested children (non-text), render recursively
      if (child.children) {
        return <React.Fragment key={i}>{renderChildren(child.children)}</React.Fragment>;
      }

      return null;
    });

  return content.map((block, idx) => {
    // ✅ Paragraph
    if (block.type === "paragraph") {
      return (
        <p
          key={idx}
          style={{
            fontSize: "16px",
            fontWeight: 400,
            letterSpacing: "0.48px",
            lineHeight: "170%",
            color: "black",
          }}
        >
          {renderChildren(block.children)}
        </p>
      );
    }

    // ✅ Unordered List
    if (block.type === "list" && block.format === "unordered") {
      return (
        <ul
          key={idx}
          className="list-disc pl-6 space-y-2 text-base text-black-800 mb-4"
        >
          {block.children?.map((li, i) => (
            <li key={i}>{renderChildren(li.children)}</li>
          ))}
        </ul>
      );
    }

    // ✅ Heading
    if (block.type === "heading") {
      const headingLevel = block.level || 3;
      const HeadingTag = `h${headingLevel}`;
      return (
        <HeadingTag
          key={idx}
          className={`text-${headingLevel + 2}xl font-bold mb-4 leading-tight`}
        >
          {renderChildren(block.children)}
        </HeadingTag>
      );
    }

    // ✅ Fallback
    return null;
  });
}

const IndustryStatsHighlights = ({ data }) => {
  const [expanded, setExpanded] = useState(false);

  if (!data) return null;

  const { titile, head_title, description, details } = data;

  // ✅ Flatten all text from description to measure total length
  const fullText =
    description
      ?.map(
        (block) => block.children?.map((child) => child.text).join("") || ""
      )
      .join(" ") || "";

  const TRUNCATE_LIMIT = 480;
  const shouldTruncate = fullText.length > TRUNCATE_LIMIT;

  // ✅ Create truncated version while preserving structure
  const truncatedDescription = (() => {
    if (!shouldTruncate) return description;

    let remaining = TRUNCATE_LIMIT;
    const truncated = [];

    for (const block of description) {
      const newChildren = [];

      for (const child of block.children) {
        if (remaining <= 0) break;

        const text = child.text || "";
        if (text.length <= remaining) {
          newChildren.push(child);
          remaining -= text.length;
        } else {
          newChildren.push({ ...child, text: text.slice(0, remaining) + "..." });
          remaining = 0;
          break;
        }
      }

      truncated.push({ ...block, children: newChildren });
      if (remaining <= 0) break;
    }

    return truncated;
  })();

  return (
    <div className="mb-6 mt-20 self-stretch rounded-[20px] justify-center items-start gap-10 inline-flex">
      <div className="py-4 justify-center items-center gap-5 grid grid-flow-col grid-cols-12">
        {/* Left Content */}
        <div className="col-span-12 self-stretch pr-10 gap-6 flex-col justify-center items-start inline-flex">
          {/* Heading */}
          <div className="w-full text-center">
            <h2 className="float-left text-xl leading-[39px] font-bold text-black">
              {head_title}
            </h2>
          </div>


          {/* Rich Text Description */}
          <div className="flex flex-col gap-4 text-sm text-neutral-700 mt-4">
            {Array.isArray(description) ? (
              <>
                {renderTextFromRichContent(
                  expanded ? description : truncatedDescription
                )}

                {shouldTruncate && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-[#0096AC] underline underline-offset-4 text-sm mt-2"
                  >
                    {expanded ? "Read Less" : "Read More"}
                  </button>
                )}
              </>
            ) : (
              <p>{description}</p>
            )}
          </div>

          {/* Stats Cards */}
          <div className="self-stretch justify-start items-start gap-8 inline-flex flex-wrap">
            {details?.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#ffda73] grow md:h-full shrink basis-0 p-5 rounded-xl shadow flex-col justify-start items-start inline-flex"
              >
                <div className="md:h-full flex flex-col gap-3">
                  <div className="relative h-9 w-11 flex items-center justify-center">
                    {item.icon?.data?.attributes?.url ? (
                      <Image
                        src={getImageUrl(item.icon.data.attributes)}
                        alt={item.icon.data.attributes.name || "icon"}
                        fill
                        sizes="44px"
                        unoptimized
                        className="object-contain"
                      />
                    ) : (
                      <div className="text-secondary-200 text-[32px]">🧩</div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="text-xl font-bold text-neutral-800 leading-7">
                      {item.pointers}
                    </div>
                    <div className="text-sm text-neutral-600 leading-[22px]">
                      {item.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryStatsHighlights;
